import Head from 'next/head'
import { useRouter } from 'next/router'
import { useStaticData } from '@mc/static-data-react'
import {
  DEFAULT_THUMBNAIL,
  SITE_NAME,
  TWITTER_CARD,
  TWITTER_HANDLE,
} from 'libs/constants'
import { getUrlWithoutQueryParams } from 'libs/utils'

type StructuredDataEntity = {
  '@type': string
  name?: string
  description?: string
  keywords?: string[]
  articleSection?: string
}

type Article = {
  heroImage?: { url?: string | null } | null
  category?: { name?: string | null } | null
  articleStructuredData?: StructuredDataEntity[]
}

// Escaping `<` keeps a `</script>` inside article text from closing the tag.
const toJsonLd = (entity: StructuredDataEntity) =>
  JSON.stringify(entity).replace(/</g, '\\u003c')

const Meta = () => {
  const { article } = useStaticData<{ article: Article }>()
  const { asPath } = useRouter()
  const structuredData = article.articleStructuredData ?? []
  const articleMeta = structuredData.find(
    (entity) => entity['@type'] === 'Article',
  )
  const keywords = structuredData.find((entity) => entity.keywords)?.keywords
  const categoryName = article.category?.name
  const jsonLdEntities = structuredData.map((entity) =>
    entity === articleMeta && categoryName && !entity.articleSection
      ? { ...entity, articleSection: categoryName }
      : entity,
  )

  if (!articleMeta) {
    return null
  }

  const getTitle = () => {
    const year = new Date().getFullYear()
    return `${articleMeta.name} - ${year} - ${SITE_NAME}`
  }

  const title = getTitle()
  const image = article.heroImage?.url || DEFAULT_THUMBNAIL

  return (
    <Head>
      <title>{title}</title>

      {keywords && <meta content={keywords.join(', ')} name='keywords' />}
      <meta content={articleMeta.description} name='description' />
      <meta content={getUrlWithoutQueryParams(asPath)} property='og:url' />
      <meta content={SITE_NAME} property='og:site_name' />
      <meta content={articleMeta.name} property='og:caption' />
      <meta content={title} property='og:title' />
      <meta content={articleMeta.description} property='og:description' />
      <meta content={image} property='og:image' />
      <meta content={TWITTER_CARD} name='twitter:card' />
      <meta content={TWITTER_HANDLE} name='twitter:site' />
      <meta content={title} name='twitter:title' />
      <meta content={articleMeta.description} name='twitter:description' />
      <meta content={image} name='twitter:image' />
      {jsonLdEntities.map((entity, index) => (
        <script
          key={`${entity['@type']}-${index}`}
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: toJsonLd(entity) }}
        />
      ))}
    </Head>
  )
}

export default Meta
